import {
  InsuranceMonitor,
  InsuranceMonitorStatus,
  PayeeRequirementPayerOverrideStatus
} from "@wingspanhq/wingspan-api-sdk";

import {
  getRequirementDefinitionTableCellProps,
  getRequirementDefinitionTemplateByParams
} from "../../../../../RequirementDefinitions/utils";
import { RequirementInstructionsView } from "../../../../components/RequirementInstructionsView/RequirementInstructionsView";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { InsuranceCoverageDetails } from "../../components/InsuranceCoverageDetails";
import { InsuranceCoverageDetailsWidgetContainer } from "../../components/InsuranceCoverageDetailsWidgetContainer";
import { selectorIsInsuranceMonitorInProgress } from "../../selectors/selectorIsInsuranceMonitorInProgress";
import { selectorPolicyTypesFromMonitor } from "../../selectors/selectorPolicyTypesFromMonitor";
import { getInsuranceCoveragePayerRequirementStatus } from "../helpers/getInsuranceCoveragePayerRequirementStatus";
import { getInsuranceCoverageProgressSteps } from "../helpers/getInsuranceCoverageProgressSteps";
import { getInsuranceCoverageRequirementName } from "../helpers/getInsuranceCoverageRequirementName";

import { ProcessingDetails } from "./ProcessingDetails";

const DEFAULT_NAME = "Insurance Coverage";

export const usePayerInsuranceCoverageConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  InsuranceMonitor | undefined
> => {
  return {
    isActionRequired: () => {
      return false;
    },
    requirementName: (requirement, context, insuranceMonitor) =>
      getInsuranceCoverageRequirementName(
        insuranceMonitor,
        requirement.name || DEFAULT_NAME
      ),

    requirementInstructions: (requirement, context, insuranceMonitor) => {
      return instructions => {
        const templateInstructions = getRequirementDefinitionTemplateByParams({
          type: requirement.type,
          policyType: selectorPolicyTypesFromMonitor(insuranceMonitor)
        }).instructions;

        return (
          <RequirementInstructionsView
            instructions={templateInstructions || instructions || ""}
          />
        );
      };
    },

    requirementDetailsProgressWidget: (
      requirement,
      context,
      insuranceMonitor
    ) => {
      const steps = getInsuranceCoverageProgressSteps(insuranceMonitor);
      return steps;
    },

    requirementDetailsWidgets(_requirement, _context, insuranceMonitor) {
      return insuranceMonitor ? (
        <InsuranceCoverageDetailsWidgetContainer
          context={RequirementContextType.Payer}
          insuranceMonitor={insuranceMonitor}
        />
      ) : null;
    },

    requirementDetails: (requirement, context, insuranceMonitor) => {
      if (
        requirement.payerOverrideStatus ===
        PayeeRequirementPayerOverrideStatus.PENDING_COMPLETION
      ) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payer}
            description="The policy you submitted for client approval was rejected"
            insuranceMonitor={insuranceMonitor!}
            pill={{
              theme: "error",
              text: "Rejected",
              icon: true
            }}
            requirement={requirement}
          />
        );
      } else if (
        insuranceMonitor &&
        selectorIsInsuranceMonitorInProgress(insuranceMonitor)
      ) {
        return {
          header: {
            label: {
              text: "Action required"
            },
            value: {
              pill: {
                theme: "neutral",
                text: "Not started",
                icon: "block"
              }
            }
          },
          divider: false,
          data: [],
          children: (
            <ProcessingDetails
              callback={callback}
              insuranceMonitor={insuranceMonitor}
              requirement={requirement}
            />
          )
        };
      } else if (insuranceMonitor?.status === InsuranceMonitorStatus.COMPLETE) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payer}
            description="A policy was found in your resources that matches the client requirement."
            insuranceMonitor={insuranceMonitor}
            pill={{
              theme: "success",
              text: "Passed",
              icon: true
            }}
            requirement={requirement}
          />
        );
      } else if (
        insuranceMonitor?.status === InsuranceMonitorStatus.FORCE_MATCHED
      ) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payer}
            description="You have opted to submit a partially matched policy for client approval."
            insuranceMonitor={insuranceMonitor}
            pill={{
              theme: "warning",
              text: "Partial match",
              icon: true
            }}
            requirement={requirement}
          />
        );
      }

      return {
        data: [],
        children: <></>
      };
    },

    toolbarActions: () => {
      return [];
    },

    nameCell: (requirement, _context, insuranceMonitor) => ({
      text: getInsuranceCoverageRequirementName(
        insuranceMonitor,
        requirement.name || DEFAULT_NAME
      )
    }),

    typeCell: (requirement, context, insuranceMonitor) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type,
        policyType: selectorPolicyTypesFromMonitor(insuranceMonitor)
      }),

    statusCell: (requirement, context, insuranceMonitor) => {
      return getInsuranceCoveragePayerRequirementStatus(
        requirement,
        insuranceMonitor
      );
    }
  };
};
